import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/layout/AppShell';
import { AuthShell } from '@/layout/AuthShell';
import { AccessDeniedPage } from '@/pages/auth/AccessDeniedPage';
import { ActivateAccountPage } from '@/pages/auth/ActivateAccountPage';
import { ChangePasswordPage } from '@/pages/auth/ChangePasswordPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { LockoutPage } from '@/pages/auth/LockoutPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { MfaChallengePage } from '@/pages/auth/MfaChallengePage';
import { MfaSetupPage } from '@/pages/auth/MfaSetupPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotFoundPage } from '@/pages/error/NotFoundPage';
import { ServerErrorPage } from '@/pages/error/ServerErrorPage';
import { ProfilePage } from '@/pages/user/ProfilePage';
import { RequirePermission } from '@/auth/RequirePermission';
import { Permission } from '@/auth/permissions';
import { AuditLogsPage } from '@/pages/admin/AuditLogsPage';
import { DataImportPage } from '@/pages/admin/DataImportPage';
import { LocalizationManagementPage } from '@/pages/admin/LocalizationManagementPage';
import { MenuManagementPage } from '@/pages/admin/MenuManagementPage';
import { RolePermissionsPage } from '@/pages/admin/RolePermissionsPage';
import { RolesPage } from '@/pages/admin/RolesPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { DealersPage } from '@/pages/stla/DealersPage';
import { RegionalPage } from '@/pages/stla/RegionalPage';
import { TakataCallsPage } from '@/pages/stla/TakataCallsPage';
import { TakataDealersPage } from '@/pages/stla/TakataDealersPage';
import { UserAccessPage } from '@/pages/stla/UserAccessPage';

/**
 * The route table.
 *
 * Paths are the Razor `@page` routes verbatim — `/auth-login`, not `/login` —
 * so bookmarks, the redirect middleware and the links in account emails all keep
 * resolving after a page moves from Razor to React. The two shells mirror the
 * two Razor layouts: `_AuthLayout` for the pages reached without a session,
 * `_VerticalLayout` for everything else.
 *
 * The auth screens are real as of Phase 2; everything else is still a
 * placeholder. The tree is what matters: the guard nesting below is where
 * authentication and permission checks live, so later phases only swap elements.
 *
 * There is no `/auth-register` route, and there is no registration page at all:
 * accounts are created by an administrator, who sends the new user a
 * set-password link.
 */
const router = createBrowserRouter([
  {
    element: <AuthShell />,
    children: [
      { path: '/auth-login', element: <LoginPage /> },
      { path: '/auth-forgotpassword', element: <ForgotPasswordPage /> },
      { path: '/auth-resetpassword', element: <ResetPasswordPage /> },
      { path: '/auth-activateaccount', element: <ActivateAccountPage /> },
      { path: '/auth-lockout', element: <LockoutPage /> },
      // Anonymous on purpose: the password was accepted but no application
      // cookie exists yet, so the caller holds only Identity's two-factor cookie.
      { path: '/auth-mfa-challenge', element: <MfaChallengePage /> },
      // Both Razor pages declared `Layout = "_AuthLayout"`, so neither belongs
      // under the app chrome: a forced password change must not render a menu
      // the user cannot use, and the refusal page is reachable anonymously.
      { path: '/auth-accessdenied', element: <AccessDeniedPage /> },
      // Where UseExceptionHandler re-executes. On the auth shell deliberately:
      // a 500 can be the app shell itself failing, so this must not need it.
      { path: '/error-500', element: <ServerErrorPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/auth-changepassword', element: <ChangePasswordPage /> },
          // Signed in, but held here by the guard when the role mandates MFA.
          // On the auth shell for the same reason as the change-password screen:
          // it must not render a menu the user is not allowed to use yet.
          { path: '/auth-mfa-setup', element: <MfaSetupPage /> },
        ],
      },
    ],
  },
  {
    element: <AppShell />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: '/user-profile', element: <ProfilePage /> },
          // Stellantis (Phase 3). The roles repeat the [Authorize] on each
          // controller; the attribute is still what enforces them.
          {
            element: <RequirePermission roles={['Admin']} />,
            children: [{ path: '/stla/admin/user-access', element: <UserAccessPage /> }],
          },
          {
            element: <RequirePermission roles={['Admin', 'Manager', 'User', 'Dealer User']} />,
            children: [{ path: '/stla/takata/calls', element: <TakataCallsPage /> }],
          },
          // Admin screens (Phase 4). Each route repeats the permission its
          // controller enforces; the attribute is still what enforces it.
          {
            element: <RequirePermission permission={Permission.users.view} />,
            children: [{ path: '/auth/users', element: <UsersPage /> }],
          },
          {
            element: <RequirePermission permission={Permission.roles.view} />,
            children: [{ path: '/auth/roles', element: <RolesPage /> }],
          },
          {
            element: <RequirePermission permission={Permission.roles.managePermissions} />,
            children: [{ path: '/auth/rolepermissions', element: <RolePermissionsPage /> }],
          },
          {
            element: <RequirePermission permission={Permission.auditLogs.view} />,
            children: [{ path: '/admin/audit-logs', element: <AuditLogsPage /> }],
          },
          {
            element: <RequirePermission permission={Permission.menus.view} />,
            children: [{ path: '/admin/menu-management', element: <MenuManagementPage /> }],
          },
          {
            // The Razor page is [Authorize(Roles = "Admin")] and the controller
            // allows Admin or Manager; the stricter of the two is the page's.
            element: <RequirePermission roles={['Admin']} />,
            children: [{ path: '/admin/localization', element: <LocalizationManagementPage /> }],
          },
          {
            // CSV import for the mystery-calls campaigns, replacing the
            // stla-takata-import CLI. Admin only, like ImportsController and
            // ImportHub — both of which enforce it independently of this guard.
            element: <RequirePermission roles={['Admin']} />,
            children: [{ path: '/admin/data-import', element: <DataImportPage /> }],
          },
          {
            // No 'Dealer User' here: the dealer dashboards plot every dealer in
            // the wave, which is not a view a dealer-scoped account gets.
            element: <RequirePermission roles={['Admin', 'Manager', 'User']} />,
            children: [
              { path: '/stla/takata', element: <TakataDealersPage /> },
              { path: '/stla/index', element: <DealersPage /> },
              { path: '/stla/regional', element: <RegionalPage /> },
            ],
          },
        ],
      },
      // Both the explicit path the Razor page published and the catch-all, so a
      // bookmarked /error-404 still resolves.
      { path: '/error-404', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
